/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FlatList, Modal, SafeAreaView, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import HeaderView from '@airasia-phoenix/react-native/widgets/HeaderView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import PressableView from '@airasia-phoenix/react-native/components/PressableView';
import type { PressableRenderState } from '@airasia-phoenix/react-native/components/PressableView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import type { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import { CurrenciesView } from '@airasia-rn/ui';
import { CurrencyItem } from '@airasia-rn/ui/widgets';
import {
  getColor,
  getShape,
  pd,
} from '@airasia-phoenix/react-native/utils/utils';
import { useTranslation } from 'react-i18next';
import { responsiveTextStyle } from '../../utils/responsiveTextStyle';

const PRESSED_COLOUR = pd.ref.palette.green10;
const LIST_CONTAINER_PADDING = pd.ref.space.spacing40.px;

export type LocaleCurrencyMobilePageProps = {
  visible: boolean;
  onClose: () => void;
  defaultTab?: 'currency' | 'language';
  // Accepted for API parity with LocaleCurrencyModal — mobile has no tab UI.
  onTabChange?: (tab: 'currency' | 'language') => void;
  currencies: CurrencyItem[];
  selectedCurrency: CurrencyItem;
  onCurrencySelect: (item: CurrencyItem) => void;
  locale: MemoizedListViewProps[];
  selectedLanguage: MemoizedListViewProps;
  onLanguageSelect: (item: MemoizedListViewProps) => void;
};

const LocaleCurrencyMobilePage: React.FC<LocaleCurrencyMobilePageProps> = ({
  visible,
  onClose,
  defaultTab = 'currency',
  currencies,
  selectedCurrency,
  onCurrencySelect,
  locale,
  selectedLanguage,
  onLanguageSelect,
}) => {
  const { t } = useTranslation('account');
  const [selectedLanguageId, setSelectedLanguageId] = useState<string | null>(
    selectedLanguage?.id ?? null,
  );

  useEffect(() => {
    setSelectedLanguageId(selectedLanguage?.id ?? null);
  }, [selectedLanguage?.id]);

  const handleLanguageSelect = useCallback(
    (item: MemoizedListViewProps) => {
      setSelectedLanguageId(item.id);
      onLanguageSelect(item);
    },
    [onLanguageSelect],
  );

  const getLanguageItemStyle = useCallback(
    (isSelected: boolean) =>
      ({ isHovered, isPointerDown }: PressableRenderState) => [
        styles.languageItem,
        (isHovered || isSelected || isPointerDown) &&
          getShape(pd.sys.shape.corner.small),
        isSelected && {
          boxShadow: [
            {
              inset: true,
              offsetX: 0,
              offsetY: 0,
              spreadDistance: 2,
              color: getColor(pd.sys.colour.secondary),
            },
          ],
        },
        {
          backgroundColor: isPointerDown
            ? PRESSED_COLOUR
            : isHovered || isSelected
              ? getColor(pd.sys.colour.secondary_low)
              : getColor(pd.sys.colour.container_none),
        },
      ],
    [styles],
  );

  const renderLanguageItem = useCallback(
    ({ item }: { item: MemoizedListViewProps }) => {
      const isSelected = item.id === selectedLanguageId;
      const title =
        item.primaryText?.split(' - ').slice(1).join(' - ') ||
        item.primaryText ||
        item.id;

      return (
        <PressableView
          onPress={() => handleLanguageSelect(item)}
          style={getLanguageItemStyle(isSelected)}
        >
          <View style={styles.languageInfo}>
            <TextView
              text={title}
              style={styles.languageTitle}
              numberOfLines={1}
            />
            {isSelected && (
              <View style={styles.selectedItemIconContainer}>
                <IconView
                  icon={pd.icon.main.navigation.check}
                  width={24}
                  height={24}
                  tint={getColor(pd.sys.colour.secondary)}
                />
              </View>
            )}
          </View>
        </PressableView>
      );
    },
    [getLanguageItemStyle, handleLanguageSelect, selectedLanguageId, styles],
  );

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <SafeAreaView style={styles.pageContainer}>
        {defaultTab === 'language' ? (
          <>
            <HeaderView
              titleStart={t('currencyView.language')}
              startIcon={{
                icon: { icon: pd.icon.main.navigation.arrow_back_platform },
                onPress: onClose,
              }}
            />
            <View style={[styles.mainContainer, styles.flexFill]}>
              <FlatList
                data={locale}
                keyExtractor={(item) => item.id.toString()}
                renderItem={renderLanguageItem}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.listContentContainer}
              />
            </View>
          </>
        ) : (
          <View style={[styles.mainContainer, styles.flexFill]}>
            <CurrenciesView
              currencyList={currencies}
              selectedValue={selectedCurrency}
              onSelect={onCurrencySelect}
              onClose={onClose}
              currencyTitle={t('currencyView.currency')}
              sortSelectedToTop={false}
              showSearch={false}
              contentPaddingBottom={0}
              contentPaddingHorizontal={LIST_CONTAINER_PADDING}
            />
          </View>
        )}
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create((pd) => ({
  pageContainer: {
    flex: 1,
    width: '100%',
    backgroundColor: getColor(pd.sys.colour.bg_primary),
  },
  mainContainer: {
    paddingBottom: pd.ref.space.spacing40.px,
  },
  flexFill: {
    flex: 1,
  },
  languageItem: {
    marginBottom: pd.ref.space.spacing20.px,
    backgroundColor: getColor(pd.sys.colour.container_none),
  },
  listContentContainer: {
    paddingHorizontal: LIST_CONTAINER_PADDING,
  },
  languageInfo: {
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: pd.ref.space.spacing20.px,
    paddingHorizontal: pd.ref.space.spacing40.px,
  },
  selectedItemIconContainer: {
    marginLeft: pd.ref.space.spacing20.px,
  },
  languageTitle: responsiveTextStyle(
    pd.sys.typescale.body_text,
    pd.sys.typescale.heading_4,
    pd.sys.colour.content_primary,
  ),
}));

export default LocaleCurrencyMobilePage;
